import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  canDeleteMessage,
  canEditMessage,
  renderSignature,
  type AgentRun,
  type Contact,
  type Conversation,
  type ConversationDetail,
  type ConversationStatus,
  type Message,
  type MessageActor,
  type QuotedMessage,
  type SupportSettings,
  type UserRole,
} from '@supporchat/shared';
import { api } from '../lib/api';
import { renderWhatsappBold } from '../lib/whatsapp-text';
import { Avatar } from '../components/Avatar';
import { formatPhone } from '../components/CallBar';
import { AttachmentPicker, attachmentProblem } from '../components/AttachmentPicker';
import { AttachmentPreview } from '../components/AttachmentPreview';
import { MediaAttachment } from '../components/MediaAttachment';
import { MessageMenu, copyText, type MessageMenuAction } from '../components/MessageMenu';
import { RemoteAccessPanel } from '../components/RemoteAccessPanel';
import { VoiceRecorderButton } from '../components/VoiceRecorderButton';
import type { CallsApi } from '../hooks/useCalls';
import { useTypingSignal } from '../hooks/useTypingSignal';

interface Props {
  currentUserId: string;
  currentUserName: string;
  /** Decide, com o id, quais mensagens enviadas o atendente pode editar ou apagar. */
  currentUserRole: UserRole;
  canWrite: boolean;
  /** Exclusão definitiva é permissão nominal, concedida por id no servidor. */
  canDelete: boolean;
  calls: CallsApi;
  conversationEvent: Conversation | null;
  messageEvent: { conversationId: string; message: Message } | null;
  /** Um atendimento foi excluído — aqui ou por outra pessoa. */
  deletedEvent: { conversationId: string; at: number } | null;
  /** "Abra este atendimento", pedido pelo monitor, pelo kanban ou por uma ligação. */
  focusRequest: { conversationId: string; at: number } | null;
  onPendingCountChange: (count: number) => void;
  onOpenConversationChange: (conversationId: string | null) => void;
}

type Filter = ConversationStatus | 'all';

/** Rótulo do tipo, acima do arquivo. */
const MEDIA_LABELS: Partial<Record<Message['type'], string>> = {
  image: '📷 Imagem',
  video: '🎬 Vídeo',
  document: '📎 Documento',
  sticker: '🙂 Figurinha',
};

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: 'open', label: 'Abertos' },
  { id: 'pending_human', label: 'Aguardando' },
  { id: 'closed', label: 'Encerrados' },
  { id: 'all', label: 'Todos' },
];

export function InboxPage({
  currentUserId,
  currentUserName,
  currentUserRole,
  canWrite,
  canDelete,
  calls,
  conversationEvent,
  messageEvent,
  deletedEvent,
  focusRequest,
  onPendingCountChange,
  onOpenConversationChange,
}: Props) {
  const [filter, setFilter] = useState<Filter>('open');
  const [search, setSearch] = useState('');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [runs, setRuns] = useState<AgentRun[]>([]);
  const [draft, setDraft] = useState('');
  /**
   * Arquivo escolhido e ainda não enviado.
   *
   * Fica aqui, e não no componente do anexo, porque ele compartilha a caixa de
   * texto: com um arquivo em espera, o que o atendente digita é a legenda dele.
   * Dois campos de texto no compositor — um para mensagem, outro para legenda —
   * seriam duas formas de escrever a mesma coisa.
   */
  const [attachment, setAttachment] = useState<File | null>(null);
  /**
   * Mensagem a que a próxima resposta vai citar — o "responder" do WhatsApp.
   *
   * Como o anexo, é do compositor e de uma conversa só: sai junto com o envio e
   * com a troca de atendimento.
   */
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  /** Bolha que acabou de ser alcançada pela citação, destacada por um instante. */
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  /**
   * Confirmação da exclusão do atendimento aberto.
   *
   * Uma faixa dentro da conversa, e não um `confirm()` do navegador: o painel vive
   * num iframe do sistema principal, e diálogo nativo ali trava a página toda.
   */
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /** Aviso de que a conversa aberta foi excluída — por mim ou por outra aba. */
  const [deletedNotice, setDeletedNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [support, setSupport] = useState<SupportSettings | null>(null);
  /**
   * Versão da foto de cada atendente, por id.
   *
   * Carregado uma vez: são poucos atendentes e a foto de trabalho não muda no meio
   * do turno. A mensagem diz quem escreveu, mas não quando aquela pessoa trocou a
   * foto — e sem versão o navegador serviria a imagem antiga do cache.
   */
  const [avatarVersions, setAvatarVersions] = useState<Record<string, string>>({});
  const messagesRef = useRef<HTMLDivElement>(null);
  const draftRef = useRef<HTMLTextAreaElement>(null);
  /**
   * O atendente está vendo o fim da conversa.
   *
   * Em `ref` e não em estado: é lido no efeito que rola e escrito a cada evento
   * de rolagem — como estado, cada pixel rolado viraria um render da thread
   * inteira.
   */
  const atBottomRef = useRef(true);

  /**
   * O "digitando..." que o cliente vê no WhatsApp enquanto o atendente escreve.
   *
   * Ligado ao rascunho, e não ao foco do campo: o que interessa ao cliente é
   * alguém escrevendo, não alguém com o cursor parado na caixa de texto.
   */
  const typing = useTypingSignal(selectedId, canWrite);

  /**
   * Devolve o foco à caixa de texto.
   *
   * A caixa é desabilitada enquanto a mensagem sai, e campo desabilitado perde o
   * foco — que o React não devolve sozinho quando ele volta a aceitar digitação.
   * Sem isto, atender é escrever, Enter, **clicar**, escrever, Enter, clicar: um
   * clique por mensagem, em cima da mesma caixa que já estava selecionada.
   *
   * Em `requestAnimationFrame` porque `setSending(false)` é uma intenção, não uma
   * mudança já aplicada: no instante seguinte a chamada o campo ainda está
   * `disabled` no DOM, e `focus()` num campo desabilitado não faz nada.
   */
  const focusDraft = useCallback(() => {
    requestAnimationFrame(() => draftRef.current?.focus());
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const result = await api.listConversations({ status: filter, search: search || undefined });
      setConversations(result.items);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [filter, search]);

  // Busca com debounce: digitar não deve disparar uma requisição por tecla.
  useEffect(() => {
    const timer = window.setTimeout(() => void loadConversations(), search ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [loadConversations, search]);

  const loadDetail = useCallback(async (id: string) => {
    try {
      const [conversation, agentRuns] = await Promise.all([
        api.getConversation(id),
        api.agentRuns(id).catch(() => [] as AgentRun[]),
      ]);
      setDetail(conversation);
      setRuns(agentRuns);
      if (conversation.unreadCount > 0) {
        await api.markRead(id);
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    if (selectedId) void loadDetail(selectedId);
    else setDetail(null);
    onOpenConversationChange(selectedId);
    // Anexo é de uma conversa só: trocar de atendimento com um arquivo em espera
    // é o caminho para mandar a foto de um cliente para outro.
    setAttachment(null);
    setReplyTo(null);
    // Pergunta de exclusão não atravessa a troca de conversa: seria uma
    // confirmação aberta apontando para o atendimento errado.
    setConfirmingDelete(false);
    if (selectedId) setDeletedNotice(null);
  }, [selectedId, loadDetail, onOpenConversationChange]);

  /**
   * Conversa aberta: o cursor já vai para a caixa de texto.
   *
   * Depende do `detail` e não do `selectedId` porque a caixa só existe depois de
   * a conversa carregar — focar antes disso não focaria nada. É o mesmo hábito do
   * WhatsApp Web: clicar no atendimento e já começar a escrever.
   */
  useEffect(() => {
    if (detail && canWrite) focusDraft();
  }, [detail?.id, canWrite, focusDraft]);

  // Card aberto no monitor ou no kanban: a conversa é carregada mesmo que o
  // filtro atual não a mostre na lista ao lado.
  useEffect(() => {
    if (focusRequest) setSelectedId(focusRequest.conversationId);
  }, [focusRequest]);

  useEffect(() => {
    void (async () => {
      try {
        setSupport(await api.getSupportSettings());
      } catch {
        // Sem as configurações o painel funciona igual; só não mostra a prévia
        // da assinatura, que é informação de apoio.
      }

      try {
        const versions = await api.userAvatarVersions();
        setAvatarVersions(Object.fromEntries(versions.map((item) => [item.userId, item.avatarUpdatedAt])));
      } catch {
        // Sem as versões, as bolhas mostram as iniciais dos atendentes — o nome
        // continua escrito ao lado, que é o que importa.
      }
    })();
  }, []);

  // ── eventos de tempo real ──

  useEffect(() => {
    if (!conversationEvent) return;

    setConversations((current) => {
      const others = current.filter((item) => item.id !== conversationEvent.id);
      const matchesFilter = filter === 'all' || conversationEvent.status === filter;
      if (!matchesFilter) return others;
      return [conversationEvent, ...others].sort(byRecency);
    });

    setDetail((current) =>
      current && current.id === conversationEvent.id ? { ...current, ...conversationEvent } : current,
    );
  }, [conversationEvent, filter]);

  /**
   * Um atendimento excluído sai da lista, e se era o que estava aberto a conversa
   * é fechada com um aviso no lugar.
   *
   * Sem isso, o painel continuaria mostrando as mensagens de um ticket que já não
   * existe — e a primeira tentativa de responder morreria em 404, sem explicar por
   * quê.
   */
  useEffect(() => {
    if (!deletedEvent) return;

    setConversations((current) => current.filter((item) => item.id !== deletedEvent.conversationId));
    setConfirmingDelete(false);

    setSelectedId((current) => {
      if (current !== deletedEvent.conversationId) return current;
      setDetail(null);
      setDeletedNotice('Este atendimento foi excluído.');
      return null;
    });
  }, [deletedEvent]);

  useEffect(() => {
    if (!messageEvent) return;

    setDetail((current) => {
      if (!current || current.id !== messageEvent.conversationId) return current;

      const index = current.messages.findIndex((message) => message.id === messageEvent.message.id);

      // Id conhecido significa atualização (áudio transcrito) ou o eco da
      // mensagem que este painel acabou de enviar: substituir cobre os dois.
      if (index >= 0) {
        const messages = [...current.messages];
        messages[index] = messageEvent.message;
        return { ...current, messages };
      }

      return { ...current, messages: [...current.messages, messageEvent.message] };
    });
  }, [messageEvent]);

  useEffect(() => {
    const pending = conversations.filter((item) => item.status === 'pending_human').length;
    onPendingCountChange(pending);
  }, [conversations, onPendingCountChange]);

  /**
   * Rola para o fim quando entram mensagens novas — só se já estava no fim.
   *
   * Antes rolava sempre, o que não incomodava porque não havia como subir. Agora
   * que o histórico rola, arrastar a vista para baixo no meio de uma leitura é
   * pior que não rolar: quem está procurando o que o cliente disse ontem perde a
   * posição a cada mensagem que chega.
   */
  useEffect(() => {
    const node = messagesRef.current;
    if (node && atBottomRef.current) node.scrollTop = node.scrollHeight;
  }, [detail?.messages.length]);

  // Conversa aberta agora começa no fim, e a régua de "estava no fim" reinicia:
  // a posição de leitura da conversa anterior não vale para esta.
  useEffect(() => {
    const node = messagesRef.current;
    atBottomRef.current = true;
    if (node) node.scrollTop = node.scrollHeight;
  }, [detail?.id]);

  const lastRun = useMemo(() => runs[runs.length - 1], [runs]);

  /**
   * A mesma assinatura que o servidor vai colocar na mensagem.
   *
   * Calculada com a função compartilhada, e não montada aqui: prévia que difere
   * do que sai é pior que prévia nenhuma, porque ninguém confere depois.
   */
  const signature = useMemo(
    () =>
      support?.signatureEnabled
        ? renderSignature(support.signatureTemplate, {
            nome: currentUserName,
            setor: support.signatureDepartment,
          })
        : '',
    [support, currentUserName],
  );

  const startCall = async (conversationId: string) => {
    try {
      // O número vem do servidor já normalizado em E.164 e validado como
      // pertencente a esta empresa.
      const target = await api.dialTarget(conversationId);
      await calls.dial(target.phone, target.name);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const replaceMessage = (updated: Message) => {
    setDetail((current) =>
      current
        ? { ...current, messages: current.messages.map((m) => (m.id === updated.id ? updated : m)) }
        : current,
    );
    // A resposta em preparo cita o que a mensagem era: apagada, ela já não pode
    // ser citada, e editada, a prévia no compositor precisa do texto novo.
    setReplyTo((current) =>
      current?.id !== updated.id ? current : updated.deletedAt ? null : updated,
    );
  };

  const resendMessage = async (messageId: string) => {
    if (!detail) return;
    setError(null);
    try {
      replaceMessage(await api.resendMessage(detail.id, messageId));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  /**
   * Edição e exclusão devolvem o erro em vez de mostrá-lo no alto da conversa: a
   * bolha que pediu é quem o exibe, ao lado do texto que não foi salvo.
   */
  const editMessage = async (messageId: string, content: string) => {
    if (!detail) return;
    replaceMessage(await api.editMessage(detail.id, messageId, content));
  };

  const deleteMessage = async (messageId: string) => {
    if (!detail) return;
    replaceMessage(await api.deleteMessage(detail.id, messageId));
  };

  const startReply = (message: Message) => {
    setReplyTo(message);
    focusDraft();
  };

  /** Clique na citação: leva até a mensagem original e a destaca. */
  const jumpToMessage = (messageId: string) => {
    const node = messagesRef.current?.querySelector(`[data-message-id="${messageId}"]`);
    if (!node) return;
    node.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setHighlightedId(messageId);
    window.setTimeout(() => setHighlightedId((current) => (current === messageId ? null : current)), 1600);
  };

  const actor: MessageActor | null = canWrite ? { userId: currentUserId, role: currentUserRole } : null;

  const send = async () => {
    if (!detail || sending) return;
    if (!attachment && !draft.trim()) return;

    setSending(true);
    setError(null);
    try {
      // Com arquivo em espera, o texto é a legenda dele — não uma segunda
      // mensagem. Enviar os dois separados chegaria ao cliente como duas bolhas,
      // e a explicação apareceria desgarrada da foto que ela explica.
      const message = attachment
        ? await api.sendAttachment(detail.id, attachment, draft, replyTo?.id)
        : await api.sendMessage(detail.id, draft.trim(), replyTo?.id);

      setDetail((current) =>
        current ? { ...current, messages: [...current.messages, message] } : current,
      );
      setDraft('');
      setAttachment(null);
      setReplyTo(null);
      // A mensagem saiu: o aviso de digitação já não descreve nada, e deixá-lo
      // vivo faria o cliente esperar uma segunda mensagem que não vem.
      typing.stop();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSending(false);
      focusDraft();
    }
  };

  /**
   * Arquivo escolhido no botão, colado ou arrastado para a conversa.
   *
   * Colar é o caminho mais curto que existe para o que o suporte mais manda:
   * captura de tela. `Print` e `Ctrl+V` na caixa de texto, sem passar por salvar
   * em arquivo e procurar a pasta.
   */
  const pickAttachment = (file: File) => {
    const problem = attachmentProblem(file);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setAttachment(file);
    // Escolher pelo botão leva o foco para o botão. Trazê-lo de volta é o que
    // permite escolher o arquivo e já digitar a legenda, sem passar pelo mouse.
    focusDraft();
  };

  const changeHandler = async (handler: 'bot' | 'human') => {
    if (!detail) return;
    try {
      const updated = await api.setHandler(detail.id, handler);
      setDetail((current) => (current ? { ...current, ...updated } : current));
      await loadDetail(detail.id);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const closeConversation = async () => {
    if (!detail) return;
    try {
      await api.closeConversation(detail.id);
      await loadDetail(detail.id);
      await loadConversations();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const reopenConversation = async () => {
    if (!detail) return;
    try {
      await api.reopenConversation(detail.id);
      await loadDetail(detail.id);
      await loadConversations();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  /**
   * Apaga o atendimento aberto e todo o histórico dele.
   *
   * A tela não espera pelo evento de tempo real para se limpar: quem acabou de
   * confirmar a exclusão não pode continuar vendo as mensagens que mandou apagar.
   */
  const deleteConversation = async () => {
    if (!detail) return;
    const conversationId = detail.id;
    const numero = detail.ticketNumber;

    setDeleting(true);
    setError(null);

    try {
      await api.deleteTicket(conversationId);
      setConfirmingDelete(false);
      setSelectedId(null);
      setDetail(null);
      setConversations((current) => current.filter((item) => item.id !== conversationId));
      setDeletedNotice(`Ticket #${numero} excluído com todo o histórico.`);
    } catch (err) {
      setError((err as Error).message);
      setConfirmingDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Conversa que o atendente acabou de iniciar.
   *
   * Ela nasce aberta e em mãos de quem iniciou; se o filtro atual a esconderia
   * (encerrados, aguardando), volta para os abertos — senão a conversa criada
   * sumiria da lista no mesmo clique que a criou.
   */
  const openStartedConversation = async (conversation: Conversation) => {
    setSearch('');
    if (filter !== 'open' && filter !== 'all') setFilter('open');
    setConversations((current) => [
      conversation,
      ...current.filter((item) => item.id !== conversation.id),
    ]);
    setSelectedId(conversation.id);
    if (conversation.id === selectedId) await loadDetail(conversation.id);
  };

  const saveContact = async (changes: { name: string; phone: string }) => {
    if (!detail) return;
    const updated = await api.updateContact(detail.id, changes);
    // O contato aparece na lista e no cabeçalho: os dois precisam do valor novo.
    setDetail((current) => (current ? { ...current, contact: updated.contact } : current));
    await loadConversations();
  };

  return (
    <div className={`inbox${detail ? ' inbox--detail' : ''}`}>
      <aside className="conversation-list">
        <div className="conversation-list__filters">
          {FILTERS.map((item) => (
            <button
              key={item.id}
              className="filter-chip"
              aria-pressed={filter === item.id}
              onClick={() => setFilter(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="search-box">
          <input
            type="search"
            placeholder="Buscar por nome ou telefone"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {canWrite && <NewConversation onStarted={openStartedConversation} />}
        </div>

        <div className="conversation-list__items">
          {conversations.length === 0 && <div className="empty">Nenhuma conversa aqui.</div>}
          {conversations.map((conversation) => (
            <button
              key={conversation.id}
              className="conversation-item"
              // Pinta a barra de estado; a seleção pinta o fundo. Duas perguntas
              // diferentes ("quem atende" e "qual está aberta"), dois canais.
              data-state={conversationState(conversation)}
              aria-current={conversation.id === selectedId}
              onClick={() => setSelectedId(conversation.id)}
            >
              {/* A foto vem antes do nome porque é o que se reconhece sem ler:
                  numa lista de vinte linhas, o rosto encontra a conversa antes de
                  o olho chegar ao texto. */}
              <span className="conversation-item__avatar">
                <Avatar
                  kind="contact"
                  id={conversation.contact.id}
                  name={contactLabel(conversation.contact)}
                  version={conversation.contact.avatarUpdatedAt}
                />
              </span>
              <div className="conversation-item__head">
                <span className="conversation-item__name">
                  {conversation.contact.name ?? conversation.contact.pushName ?? conversation.contact.phone}
                </span>
                <span className="conversation-item__time">{formatTime(conversation.lastMessageAt)}</span>
              </div>
              <div className="conversation-item__preview">
                {conversation.lastMessagePreview ?? 'Sem mensagens'}
              </div>
              <div className="conversation-item__meta">
                <StatusBadge conversation={conversation} />
                {conversation.unreadCount > 0 && (
                  <span className="tab__badge">{conversation.unreadCount}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      </aside>

      <section className="thread">
        {!detail && deletedNotice && (
          <div className="alert alert--info" style={{ margin: 12 }}>
            {deletedNotice}
            <button
              className="btn btn--sm"
              style={{ marginLeft: 12 }}
              onClick={() => setDeletedNotice(null)}
            >
              Entendi
            </button>
          </div>
        )}
        {!detail && <ThreadEmpty />}

        {detail && (
          <>
            <header className="thread__header" data-state={conversationState(detail)}>
              <Avatar
                kind="contact"
                id={detail.contact.id}
                name={contactLabel(detail.contact)}
                version={detail.contact.avatarUpdatedAt}
                size="md"
              />
              <div>
                <div className="thread__title">
                  {/* O número é o que se diz ao telefone: vem antes do nome e não
                      muda quando o mesmo cliente volta. */}
                  <span className="thread__ticket mono">#{detail.ticketNumber}</span>
                  {detail.contact.name ?? detail.contact.pushName ?? detail.contact.phone ?? 'Cliente'}
                </div>
                <div className="thread__subtitle">
                  {/* Telefone é dado que se lê em voz alta para discar: agrupado e
                      monoespaçado, em vez de doze dígitos emendados. */}
                  {detail.contact.phone ? (
                    <span className="mono">{formatPhone(detail.contact.phone)}</span>
                  ) : (
                    'telefone desconhecido'
                  )}
                  {detail.escalationReason && ` · ${detail.escalationReason}`}
                  {detail.reopenCount > 0 &&
                    ` · reaberto ${detail.reopenCount}${detail.reopenCount === 1 ? ' vez' : ' vezes'}`}
                </div>
              </div>

              <div className="thread__actions">
                <StatusBadge conversation={detail} />
                {canWrite && (
                  <ContactEditor
                    key={detail.contact.id}
                    contact={detail.contact}
                    onSave={saveContact}
                  />
                )}
                {canWrite && <RemoteAccessPanel conversationId={detail.id} />}
                {canWrite && calls.available && detail.contact.phone && (
                  <button
                    className="btn btn--sm"
                    title="Ligar para o cliente pelo WhatsApp"
                    disabled={calls.state.phase !== 'idle'}
                    onClick={() => void startCall(detail.id)}
                  >
                    Ligar
                  </button>
                )}
                {canWrite && detail.status === 'closed' && (
                  <button className="btn btn--sm btn--primary" onClick={() => void reopenConversation()}>
                    Reabrir
                  </button>
                )}
                {canDelete && (
                  <button
                    className="btn btn--sm btn--ghost-danger"
                    title="Excluir este atendimento e todas as mensagens dele"
                    disabled={deleting}
                    onClick={() => setConfirmingDelete(true)}
                  >
                    Excluir
                  </button>
                )}
                {canWrite && detail.status !== 'closed' && (
                  <>
                    {detail.handler === 'bot' ? (
                      // A ação principal do cabeçalho quando o robô responde: é o
                      // que se clica ao perceber que a conversa precisa de gente.
                      <button
                        className="btn btn--sm btn--primary"
                        onClick={() => void changeHandler('human')}
                      >
                        Assumir
                      </button>
                    ) : (
                      <button className="btn btn--sm" onClick={() => void changeHandler('bot')}>
                        Devolver ao agente
                      </button>
                    )}
                    <button className="btn btn--sm btn--danger" onClick={() => void closeConversation()}>
                      Encerrar
                    </button>
                  </>
                )}
              </div>
            </header>

            {confirmingDelete && (
              <div className="alert alert--danger thread__confirm">
                <div>
                  <strong>Excluir o ticket #{detail.ticketNumber}?</strong> Apaga em definitivo as{' '}
                  {detail.messages.length === 1
                    ? 'mensagem desta conversa'
                    : `${detail.messages.length} mensagens desta conversa`}
                  , com as fotos, áudios e documentos. O cadastro do cliente continua, e o que ele
                  já recebeu no WhatsApp não é apagado do celular dele.
                </div>
                <div className="thread__confirm-actions">
                  <button
                    className="btn btn--sm"
                    disabled={deleting}
                    onClick={() => setConfirmingDelete(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn btn--sm btn--danger"
                    disabled={deleting}
                    onClick={() => void deleteConversation()}
                  >
                    {deleting ? 'Excluindo...' : 'Excluir para sempre'}
                  </button>
                </div>
              </div>
            )}

            {error && <div className="alert alert--error" style={{ margin: 12 }}>{error}</div>}

            <div
              className="thread__messages"
              ref={messagesRef}
              onScroll={(event) => {
                const node = event.currentTarget;
                // Folga de 60px: ninguém para a rolagem no pixel exato do fim, e
                // sem ela a mensagem seguinte não acompanharia.
                atBottomRef.current =
                  node.scrollHeight - node.scrollTop - node.clientHeight < 60;
              }}
            >
              {detail.messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  authorVersion={
                    message.senderUserId ? avatarVersions[message.senderUserId] ?? null : null
                  }
                  actor={actor}
                  highlighted={highlightedId === message.id}
                  onResend={canWrite ? () => void resendMessage(message.id) : undefined}
                  // Responder é escrever: só onde o compositor está aberto, e só o
                  // que já está no WhatsApp — o aparelho do cliente acha a original
                  // pelo id dela lá.
                  onReply={
                    canWrite &&
                    detail.status !== 'closed' &&
                    detail.handler === 'human' &&
                    message.sender !== 'system' &&
                    message.waMessageId &&
                    !message.deletedAt
                      ? () => startReply(message)
                      : undefined
                  }
                  onEdit={(content) => editMessage(message.id, content)}
                  onDelete={() => deleteMessage(message.id)}
                  onJumpTo={jumpToMessage}
                />
              ))}
            </div>

            {lastRun && (
              <div className="composer__hint" style={{ background: 'var(--surface-2)', color: 'var(--text-muted)' }}>
                <span>
                  Última decisão do agente: <strong>{translateAction(lastRun.action)}</strong>
                  {lastRun.confidence !== null && ` · confiança ${(lastRun.confidence * 100).toFixed(0)}%`}
                  {lastRun.reason && ` · ${lastRun.reason}`}
                </span>
              </div>
            )}

            {detail.status === 'closed' ? (
              <div className="composer__hint">
                <span>
                  Atendimento encerrado. Se o cliente escrever de novo, este mesmo ticket reabre — com
                  todo o histórico.
                </span>
                {canWrite && (
                  <button className="btn btn--sm" onClick={() => void reopenConversation()}>
                    Reabrir agora
                  </button>
                )}
              </div>
            ) : detail.handler === 'bot' ? (
              <div className="composer__hint">
                <span>O agente está respondendo esta conversa.</span>
                {canWrite && (
                  <button className="btn btn--sm" onClick={() => void changeHandler('human')}>
                    Assumir para escrever
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Faixa própria, acima do compositor: dentro dele a linha
                    disputaria espaço com a caixa de texto e os botões. */}
                {signature && (
                  <div className="composer__signature" title="Toda mensagem que você enviar sai com esta assinatura">
                    sai como {renderWhatsappBold(signature)}: e sua mensagem na linha de baixo
                  </div>
                )}

                {replyTo && (
                  <div className="composer__reply">
                    <QuoteBlock quoted={quoteOf(replyTo)} onClick={() => jumpToMessage(replyTo.id)} />
                    <button
                      type="button"
                      className="composer__reply-cancel"
                      aria-label="Cancelar resposta"
                      title="Cancelar resposta (Esc)"
                      onClick={() => setReplyTo(null)}
                    >
                      ✕
                    </button>
                  </div>
                )}

                {attachment && (
                  <AttachmentPreview
                    file={attachment}
                    sending={sending}
                    onRemove={() => setAttachment(null)}
                  />
                )}

                <div
                  className="composer"
                  // Arrastar para cima do compositor é o gesto que quem já usa
                  // WhatsApp Web tenta primeiro. `onDragOver` com `preventDefault`
                  // é o que impede o navegador de abrir o arquivo em outra aba.
                  onDragOver={(event) => {
                    if (canWrite && !sending) event.preventDefault();
                  }}
                  onDrop={(event) => {
                    // Durante o envio o arquivo em espera é o que está subindo:
                    // trocá-lo agora mandaria a legenda de um com os bytes do outro.
                    if (!canWrite || sending) return;
                    const file = event.dataTransfer.files?.[0];
                    if (!file) return;
                    event.preventDefault();
                    pickAttachment(file);
                  }}
                >
                <textarea
                  ref={draftRef}
                  placeholder={
                    attachment
                      ? 'Legenda para o arquivo (opcional) — Enter envia'
                      : 'Escreva a resposta... (Enter envia, Shift+Enter quebra linha)'
                  }
                  value={draft}
                  disabled={!canWrite || sending}
                  onChange={(event) => {
                    setDraft(event.target.value);
                    // Apagar até esvaziar é desistir de escrever: continuar
                    // avisando o cliente prometeria uma mensagem que já não existe.
                    if (event.target.value.trim()) typing.keystroke();
                    else typing.stop();
                  }}
                  onPaste={(event) => {
                    const file = Array.from(event.clipboardData.files)[0];
                    // Só arquivo é interceptado: texto colado continua sendo
                    // texto colado, inclusive quando vem de um editor rico.
                    if (!canWrite || sending || !file) return;
                    event.preventDefault();
                    pickAttachment(file);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      void send();
                    }
                    if (event.key === 'Escape' && replyTo) {
                      event.preventDefault();
                      setReplyTo(null);
                    }
                  }}
                />
                <button
                  className="btn btn--primary"
                  disabled={!canWrite || sending || (!draft.trim() && !attachment)}
                  onClick={() => void send()}
                >
                  {sending ? 'Enviando' : 'Enviar'}
                </button>

                {canWrite && (
                  <AttachmentPicker disabled={sending} onPick={pickAttachment} onError={setError} />
                )}

                {canWrite && (
                  <VoiceRecorderButton
                    conversationId={detail.id}
                    onSent={(message) =>
                      setDetail((current) =>
                        current ? { ...current, messages: [...current.messages, message] } : current,
                      )
                    }
                    onError={setError}
                  />
                )}
                </div>
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function MessageBubble({
  message,
  authorVersion,
  actor,
  highlighted,
  onResend,
  onReply,
  onEdit,
  onDelete,
  onJumpTo,
}: {
  message: Message;
  /** Versão da foto de quem escreveu; `null` quando não tem foto. */
  authorVersion: string | null;
  /** Quem está vendo — `null` para perfil de leitura, que não mexe em nada. */
  actor: MessageActor | null;
  highlighted: boolean;
  onResend?: () => void;
  onReply?: () => void;
  onEdit: (content: string) => Promise<void>;
  onDelete: () => Promise<void>;
  onJumpTo: (messageId: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const variant =
    message.sender === 'contact'
      ? 'contact'
      : message.sender === 'bot'
        ? 'bot'
        : message.sender === 'system'
          ? 'system'
          : 'agent';

  const isAudio = message.type === 'audio';
  const deleted = !!message.deletedAt;
  /**
   * Visualização única que chegou vazia — o caso comum.
   *
   * Separado do simples `viewOnce` porque de vez em quando o arquivo vem junto:
   * aí a bolha só marca o que era, sem pedir um reenvio que não é preciso.
   */
  const viewOnceLost = message.viewOnce && !message.hasMedia && !message.content.trim();
  const transcribing = isAudio && !message.viewOnce && message.transcriptionStatus === 'pending';
  // Imagem e vídeo passam pela leitura da IA; enquanto ela não volta, o arquivo
  // já pode ser visto — quem está atendendo não precisa esperar a descrição.
  const reading =
    (message.type === 'image' || message.type === 'video') &&
    !message.viewOnce &&
    message.transcriptionStatus === 'pending';

  // O prazo do WhatsApp corre enquanto a conversa está aberta: a regra é lida a
  // cada render, e o servidor a confere de novo — quem abre o menu no minuto 14 e
  // salva no 16 recebe a explicação, não uma edição que o cliente nunca veria.
  const canEdit = !!actor && canEditMessage(message, actor);
  const canDelete = !!actor && canDeleteMessage(message, actor);

  const actions: MessageMenuAction[] = [];
  if (onReply) actions.push({ id: 'reply', label: 'Responder', onSelect: onReply });
  if (message.content.trim()) {
    actions.push({
      id: 'copy',
      label: copied ? 'Copiado' : 'Copiar texto',
      onSelect: () => {
        void copyText(message.content).then((ok) => {
          if (!ok) return;
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        });
      },
    });
  }
  if (canEdit) {
    actions.push({
      id: 'edit',
      label: 'Editar',
      onSelect: () => {
        setEditDraft(message.content);
        setActionError(null);
        setConfirmingDelete(false);
        setEditing(true);
      },
    });
  }
  if (canDelete) {
    actions.push({
      id: 'delete',
      label: message.delivery === 'failed' ? 'Descartar' : 'Apagar para todos',
      danger: true,
      onSelect: () => {
        setActionError(null);
        setEditing(false);
        setConfirmingDelete(true);
      },
    });
  }

  const run = async (action: () => Promise<void>, done: () => void) => {
    setBusy(true);
    setActionError(null);
    try {
      await action();
      done();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = () => {
    const content = editDraft.trim();
    if (!content || busy) return;
    if (content === message.content) {
      setEditing(false);
      return;
    }
    void run(() => onEdit(content), () => setEditing(false));
  };

  const classes = [
    'bubble',
    `bubble--${variant}`,
    deleted && 'bubble--deleted',
    highlighted && 'bubble--highlight',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} data-message-id={message.id}>
      {variant !== 'system' && !editing && (
        <MessageMenu actions={actions} align={variant === 'contact' ? 'left' : 'right'} />
      )}

      {/* Só nas mensagens de gente, e só quando se sabe quem: o robô já tem bolha
          própria, e a do cliente tem o nome dele no cabeçalho da conversa. Com
          dois atendentes no mesmo ticket, esta linha é a única forma de saber
          quem prometeu o quê. */}
      {variant === 'agent' && message.senderName && (
        <div className="bubble__author">
          <Avatar
            kind="user"
            id={message.senderUserId}
            name={message.senderName}
            version={authorVersion}
            size="sm"
          />
          {message.senderName}
        </div>
      )}

      {/* Apagada continua legível aqui, riscada: o cliente não a vê mais, mas quem
          pega o atendimento depois precisa saber o que foi dito e retirado. */}
      {deleted && (
        <div className="bubble__deleted">
          🚫 {message.direction === 'inbound' ? 'Apagada pelo cliente' : 'Apagada para todos'}
        </div>
      )}

      {message.quoted && (
        <QuoteBlock
          quoted={message.quoted}
          onClick={message.quoted.messageId ? () => onJumpTo(message.quoted!.messageId!) : undefined}
        />
      )}

      {message.type !== 'text' && !message.viewOnce && (
        <div className="muted" style={{ fontSize: 12, marginBottom: 2 }}>
          {isAudio
            ? `🎤 Áudio${message.audioDurationSeconds ? ` · ${formatDuration(message.audioDurationSeconds)}` : ''}`
            : MEDIA_LABELS[message.type] ?? `[${message.type}]`}
        </div>
      )}

      {/*
        Visualização única: o WhatsApp não entrega esse conteúdo ao aparelho
        pareado, então a bolha chegava vazia — "[other] sem texto" — e parecia
        falha do painel. Aqui ela diz o que aconteceu e o que fazer, porque o
        único jeito de ver o que o cliente mandou é ele mandar de novo.
      */}
      {message.viewOnce && (
        <div className="bubble__viewonce">
          <strong>🔒 Mensagem de visualização única</strong>
          {viewOnceLost && (
            <span>
              O WhatsApp não entrega esse conteúdo ao painel. Peça ao cliente para reenviar sem a
              visualização única.
            </span>
          )}
        </div>
      )}

      {message.hasMedia && <MediaAttachment message={message} />}

      {message.mediaRemovedAt && (
        <div className="muted" style={{ fontSize: 12 }}>
          arquivo removido por retenção
        </div>
      )}

      {editing ? (
        <div className="bubble__edit">
          <textarea
            autoFocus
            value={editDraft}
            disabled={busy}
            // Linhas escritas mais as que a quebra automática cria (~50 letras
            // por linha na largura da bolha): contando só os Enter, um parágrafo
            // longo abria numa caixa de duas linhas, com o resto escondido.
            rows={Math.min(
              10,
              Math.max(3, editDraft.split('\n').reduce((total, line) => total + Math.ceil((line.length || 1) / 50), 0)),
            )}
            onChange={(event) => setEditDraft(event.target.value)}
            onFocus={(event) => {
              // Cursor no fim, como no WhatsApp: quase toda edição é consertar a
              // última palavra.
              const end = event.target.value.length;
              event.target.setSelectionRange(end, end);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                saveEdit();
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                setEditing(false);
              }
            }}
          />
          <div className="bubble__edit-actions">
            <span className="muted">Enter salva · Esc cancela</span>
            <button className="btn btn--sm" disabled={busy} onClick={() => setEditing(false)}>
              Cancelar
            </button>
            <button
              className="btn btn--sm btn--primary"
              disabled={busy || !editDraft.trim()}
              onClick={saveEdit}
            >
              {busy ? 'Salvando' : 'Salvar'}
            </button>
          </div>
        </div>
      ) : transcribing ? (
        <em className="muted">transcrevendo áudio...</em>
      ) : reading ? (
        <em className="muted">lendo o arquivo...</em>
      ) : message.content ? (
        <div className="bubble__text">{message.content}</div>
      ) : // "sem texto" só quando a bolha ficaria vazia de verdade: foto enviada
      // sem legenda não é mensagem faltando, é foto sem legenda — e o aviso
      // embaixo dela dizia que algo tinha dado errado.
      message.hasMedia || message.viewOnce ? null : (
        <em className="muted">sem texto</em>
      )}

      {/* Confirmação dentro da bolha, e não `confirm()`: o painel vive num iframe,
          e diálogo nativo ali trava a página toda. */}
      {confirmingDelete && (
        <div className="bubble__confirm">
          <span>
            {message.delivery === 'failed'
              ? 'Descartar? Ela não chegou ao cliente.'
              : 'Apagar para todos? O cliente deixa de ver esta mensagem.'}
          </span>
          <div className="bubble__confirm-actions">
            <button className="btn btn--sm" disabled={busy} onClick={() => setConfirmingDelete(false)}>
              Cancelar
            </button>
            <button
              className="btn btn--sm btn--danger"
              disabled={busy}
              onClick={() => void run(onDelete, () => setConfirmingDelete(false))}
            >
              {busy ? 'Apagando' : message.delivery === 'failed' ? 'Descartar' : 'Apagar'}
            </button>
          </div>
        </div>
      )}

      {actionError && <div className="bubble__delivery">{actionError}</div>}

      {/*
        Registrar e entregar são passos diferentes: sem esta linha, a mensagem
        que a fila não conseguiu enviar tem a mesma bolha da que chegou, e quem
        atendeu só descobre pelo cliente.
      */}
      {message.delivery === 'failed' && !deleted && (
        <div className="bubble__delivery" title={message.deliveryError ?? undefined}>
          <span>
            ⚠ não entregue ao cliente
            {onResend && (
              <button className="bubble__resend" onClick={onResend}>
                tentar de novo
              </button>
            )}
          </span>
          {message.deliveryError && <span className="bubble__delivery-why">{message.deliveryError}</span>}
        </div>
      )}

      {/* A mudança pedida no painel não chegou: o cliente continua vendo a versão
          de antes, e quem atende precisa saber disso antes de seguir a conversa. */}
      {message.changeError && (
        <div className="bubble__delivery">
          <span>
            {deleted ? '⚠ o cliente ainda vê esta mensagem' : '⚠ a edição não chegou ao cliente'}
          </span>
          <span className="bubble__delivery-why">{message.changeError}</span>
        </div>
      )}

      {variant !== 'system' && (
        <div className="bubble__meta">
          {/*
            Só o que foi enviado leva assinatura. Em conversa de duas pessoas, o
            nome do cliente já está no cabeçalho, e repeti-lo em cada bolha que ele
            manda ainda alarga a bolha: "Sophia 11/08" é mais largo que "oi".
          */}
          {variant !== 'contact' && (
            <span>{message.senderName ?? (message.sender === 'bot' ? 'Assistente' : '')}</span>
          )}
          <span title={formatFullDateTime(message.createdAt)}>
            {message.editedAt && (
              <span className="bubble__edited" title={`editada ${formatFullDateTime(message.editedAt)}`}>
                editada ·{' '}
              </span>
            )}
            {formatMessageTime(message.createdAt)}
            {message.delivery === 'pending' && ' · enviando'}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * A mensagem citada, no alto da bolha que responde e no compositor.
 *
 * Clicável quando a original está na conversa: numa conversa longa, "sim, pode"
 * só faz sentido junto da pergunta, e ir até ela é o que o WhatsApp também faz.
 */
function QuoteBlock({ quoted, onClick }: { quoted: QuotedMessage; onClick?: () => void }) {
  const author =
    quoted.sender === 'contact'
      ? quoted.senderName ?? 'Cliente'
      : quoted.sender === 'bot'
        ? 'Assistente'
        : quoted.senderName ?? 'Atendente';

  const content = (
    <>
      <span className="quote__author">{author}</span>
      <span className="quote__text">{quoted.preview || MEDIA_LABELS[quoted.type] || 'Mensagem'}</span>
    </>
  );

  return onClick ? (
    <button type="button" className={`quote quote--${quoted.sender}`} onClick={onClick} title="Ir até a mensagem">
      {content}
    </button>
  ) : (
    <div className={`quote quote--${quoted.sender}`}>{content}</div>
  );
}

/** A citação de uma mensagem que está no painel, montada como o servidor montaria. */
function quoteOf(message: Message): QuotedMessage {
  const text = message.content.replace(/\s+/g, ' ').trim();
  return {
    messageId: message.id,
    sender: message.sender,
    senderName: message.senderName,
    type: message.type,
    preview: text.length > 120 ? `${text.slice(0, 117)}...` : text,
  };
}

/**
 * Nenhuma conversa aberta.
 *
 * No lugar de uma frase solta no meio do vazio, a legenda das barras de estado:
 * é o código de cores pelo qual a fila é lida, e este é o único momento em que
 * há espaço e calma para aprendê-lo. Na ordem da urgência, não do alfabeto.
 */
function ThreadEmpty() {
  const states: ConversationState[] = ['pending', 'bot', 'human', 'closed'];

  return (
    <div className="thread-empty">
      <p className="thread-empty__title">Nenhuma conversa aberta</p>
      <p className="thread-empty__hint">Escolha uma na fila à esquerda.</p>

      <ul className="legend">
        {states.map((state) => (
          <li key={state} className="legend__item" data-state={state}>
            {STATE_LABEL[state]}
          </li>
        ))}
      </ul>
    </div>
  );
}

type ConversationState = 'bot' | 'human' | 'pending' | 'closed';

/**
 * De quem é a conversa.
 *
 * É a única pergunta que se faz ao varrer a fila, e a tela a responde duas vezes:
 * pela barra de estado (a cor na lateral da linha e do cabeçalho) e pela
 * etiqueta, por escrito. Uma função só para as duas, para cor e texto nunca
 * discordarem — dois `if` iguais em lugares diferentes divergem no primeiro
 * estado novo.
 */
/**
 * Como o cliente é chamado na tela: o nome que alguém digitou, o que ele mesmo
 * publicou no WhatsApp ou, na falta dos dois, o número.
 *
 * A mesma ordem da lista e do cabeçalho, aqui num lugar só — as iniciais do avatar
 * têm de vir do mesmo nome que aparece escrito ao lado, senão o círculo diz "MS" e
 * a linha diz outro nome.
 */
function contactLabel(contact: Contact): string | null {
  return contact.name ?? contact.pushName ?? contact.phone ?? null;
}

function conversationState(conversation: Conversation): ConversationState {
  if (conversation.status === 'closed') return 'closed';
  if (conversation.status === 'pending_human') return 'pending';
  return conversation.handler === 'bot' ? 'bot' : 'human';
}

const STATE_LABEL: Record<ConversationState, string> = {
  bot: 'Agente',
  human: 'Atendente',
  pending: 'Aguardando atendente',
  closed: 'Encerrado',
};

const STATE_BADGE: Record<ConversationState, string> = {
  bot: 'badge--bot',
  human: 'badge--human',
  pending: 'badge--pending',
  closed: 'badge--closed',
};

function StatusBadge({ conversation }: { conversation: Conversation }) {
  const state = conversationState(conversation);
  return <span className={`badge ${STATE_BADGE[state]}`}>{STATE_LABEL[state]}</span>;
}

/**
 * Corrige o nome e o telefone do cliente.
 *
 * O telefone é editável porque o WhatsApp não sempre o entrega: contato que
 * chega identificado por um código interno fica sem número, e sem número não há
 * como ligar. Quando falta o número, o botão diz isso — em vez de abrir o
 * formulário por conta própria sobre a conversa que o atendente está lendo.
 *
 * O formulário flutua sobre o conteúdo porque o cabeçalho é uma faixa estreita:
 * empilhar dois campos ali dentro empurraria as mensagens para baixo a cada
 * abertura.
 */
function ContactEditor({
  contact,
  onSave,
}: {
  contact: Contact;
  onSave: (changes: { name: string; phone: string }) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(contact.name ?? contact.pushName ?? '');
  const [phone, setPhone] = useState(contact.phone ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSave({ name, phone });
      setOpen(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="contact-editor">
      <button
        className={`btn btn--sm${contact.phone ? '' : ' btn--primary'}`}
        title="Editar nome e telefone do cliente"
        onClick={() => setOpen((current) => !current)}
      >
        {contact.phone ? 'Editar cliente' : 'Informar telefone'}
      </button>

      {open && (
        <form className="contact-editor__panel" onSubmit={(event) => void submit(event)}>
          <div className="field">
            <label htmlFor="contact-name">Nome</label>
            <input
              id="contact-name"
              value={name}
              placeholder={contact.pushName ?? 'Como o cliente se chama'}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="contact-phone">Telefone</label>
            <input
              id="contact-phone"
              value={phone}
              placeholder="5548988434506"
              inputMode="tel"
              autoFocus={!contact.phone}
              onChange={(event) => setPhone(event.target.value)}
            />
            <span className="field__hint">
              Com código do país e DDD. É o número usado para ligar.
            </span>
          </div>

          {error && <div className="alert alert--error">{error}</div>}

          <div className="contact-editor__actions">
            <button className="btn btn--sm btn--primary" type="submit" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
            <button className="btn btn--sm" type="button" onClick={() => setOpen(false)}>
              Cancelar
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/**
 * Escrever primeiro para um cliente, pelo número.
 *
 * Nome é opcional — o WhatsApp informa o do perfil quando o cliente responder —,
 * mas a mensagem não: o ticket só existe para quem conversa, e um contato
 * cadastrado sem mensagem seria um card vazio no quadro.
 */
function NewConversation({ onStarted }: { onStarted: (conversation: Conversation) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setOpen(false);
    setError(null);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSending(true);
    setError(null);
    try {
      const conversation = await api.startConversation({
        phone,
        name: name.trim() || undefined,
        content,
      });
      setPhone('');
      setName('');
      setContent('');
      setOpen(false);
      await onStarted(conversation);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="new-conversation">
      <button
        className="btn btn--sm btn--primary"
        title="Adicionar um contato e enviar a primeira mensagem"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        + Nova conversa
      </button>

      {open && (
        <form className="new-conversation__panel" onSubmit={(event) => void submit(event)}>
          <div className="field">
            <label htmlFor="new-conversation-phone">Telefone (WhatsApp)</label>
            <input
              id="new-conversation-phone"
              value={phone}
              placeholder="5548988434506"
              inputMode="tel"
              autoFocus
              required
              onChange={(event) => setPhone(event.target.value)}
            />
            <span className="field__hint">Com código do país e DDD.</span>
          </div>

          <div className="field">
            <label htmlFor="new-conversation-name">Nome (opcional)</label>
            <input
              id="new-conversation-name"
              value={name}
              placeholder="Como o cliente se chama"
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="new-conversation-content">Mensagem</label>
            <textarea
              id="new-conversation-content"
              value={content}
              rows={4}
              required
              placeholder="Olá! Aqui é do suporte..."
              onChange={(event) => setContent(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                  event.currentTarget.form?.requestSubmit();
                }
              }}
            />
          </div>

          {error && <div className="alert alert--error">{error}</div>}

          <div className="contact-editor__actions">
            <button
              className="btn btn--sm btn--primary"
              type="submit"
              disabled={sending || !phone.trim() || !content.trim()}
            >
              {sending ? 'Enviando...' : 'Enviar'}
            </button>
            <button className="btn btn--sm" type="button" onClick={close}>
              Cancelar
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function byRecency(a: Conversation, b: Conversation): number {
  const timeA = a.lastMessageAt ?? a.createdAt;
  const timeB = b.lastMessageAt ?? b.createdAt;
  return timeB.localeCompare(timeA);
}

function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(Math.round(seconds % 60)).padStart(2, '0')}`;
}

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();

  return sameDay
    ? date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/**
 * Horário da bolha: sempre a hora, e a data junto quando não é de hoje.
 *
 * Diferente da lista de conversas, onde a data sozinha basta para ordenar: dentro
 * da conversa, "11/08" não diz se o cliente esperou dez minutos ou a tarde toda
 * pela resposta — e é isso que quem atende procura no horário.
 */
function formatMessageTime(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  if (date.toDateString() === now.toDateString()) return time;

  const day = date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    ...(date.getFullYear() !== now.getFullYear() ? { year: '2-digit' } : {}),
  });
  return `${day} ${time}`;
}

/** Data e hora completas, para a dica ao passar o mouse sobre o horário. */
function formatFullDateTime(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function translateAction(action: AgentRun['action']): string {
  const labels: Record<AgentRun['action'], string> = {
    replied: 'respondeu',
    clarifying: 'perguntou para entender',
    courtesy: 'respondeu à saudação',
    escalated: 'transferiu para atendente',
    skipped: 'não agiu',
    failed: 'falhou',
  };
  return labels[action];
}
